import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { get } from 'lodash/fp';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import JobUpdateRequestSchema from '@careerstart/wae-common/schema/job-order/update.patch.req.json';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import BackdropCircularProgress from '../../../components/BackdropCircularProgress';
import WaeButton from '../../../components/Button';
import PaginatedDataGrid from '../../../components/DataGrid';
import DetailView from '../../../components/DetailView/DetailView2';
import ExportView from '../../../components/ExportView/ExportView';
import DateRangePickerFilter from '../../../components/Filters/DateRangePickerFilter';
import FreeSearchFilter from '../../../components/Filters/FreeSearchFilter';
import SearchableSelectDropdownFilter from '../../../components/Filters/SearchableSelectDropdownFilter';
import { validateSchema } from '../../../components/Form/validations';
import Tab from '../../../components/Tab';
import searchParamOptions from '../../../constants/searchParams';
import selectUser from '../../../store/selectors/appSelector';
import {
  selectIsCancelProcessing,
  selectIsCreateJobsDialogOpen,
  selectIsLoading,
  selectIsLoadingPathSelectedJob,
  selectIsLoadingPt,
  selectIsUpdateProcessing,
  selectJobListRowData,
  selectPositionTemplateData,
  selectSelectedJob,
  selectTotalRowCount,
  selectUsers,
} from '../../../store/selectors/jobListSelectors';
import theme, {
  dataGridFiltersHeight,
  listHeaderHeight,
  navigationBarHeight,
  tabHeight,
} from '../../../theme';
import { searchParamSelectIdHandler } from '../../../utils';
import { useDims } from '../../../utils/customHooks';
import launchDarklyToggles from '../../../utils/launchDarklyToggles';
import { showSnackbar } from '../../app';
import CreateJobsModal from '../createJobs';

import CreateJobDrawer from './CreateJobDrawer/CreateJobDrawer';
import Overview from './detail/Overview';
import RosterForm from './detail/RosterForm';
import ShiftDetails from './detail/ShiftDetails';
import AssignEmployeeModal from './AssignEmployeeModal';
import CancelJobModal from './CancelJobModal';
import filters from './filters';
import getJobListColumnData from './getJobListColumnData';
import JobTypeTab from './JobTypeTab';
import {
  getCorporationsForJobsList,
  getJobsList,
  getPathSelectedJob,
  getPositionTemplate,
  getUsers,
  setIsCreateJobsDialogOpen,
  setSelectedJob,
  updateJob,
} from './reducer';

const RootGrid = styled(Grid)(() => ({
  height: `calc(100vh - ${navigationBarHeight})`,
  position: 'relative',
  top: navigationBarHeight,
  padding: theme.spacing(2, 0),
}));

const additiveFilters = [
  {
    customFilter: {
      field: 'corporation',
      operation: 'equalsID',
      optionsAPICallback: {
        httpMethod: 'POST',
        route: 'corporations/read',
        generateBody: (searchTerm) => ({
          filters: [{ operation: 'contains', field: 'name', value: searchTerm }],
        }),
      },
      getOptionLabel: (option) => get(['name'], option),
      placeholder: 'Corporation',
      type: SearchableSelectDropdownFilter,
    },
  },
  {
    customFilter: {
      field: { start: 'start', end: 'end' },
      operation: { start: 'onOrAfter', end: 'onOrBefore' },
      placeholder: 'Date Range',
      type: DateRangePickerFilter,
    },
  },
  {
    customFilter: {
      field: 'name',
      operation: 'icontains',
      placeholder: 'Name',
      type: FreeSearchFilter,
    },
  },
];

const JobsList = ({ flags }) => {
  const dispatch = useDispatch();
  const muiTheme = useTheme();
  const mediumScreen = useMediaQuery(muiTheme.breakpoints.up('md'));
  const largeScreen = useMediaQuery('(min-width:1910px)');

  const [searchParams, setSearchParams] = useSearchParams();
  const pathSelectedJobId = searchParams.get(searchParamOptions.JOB_ORDER);
  const navigate = useNavigate();
  const location = useLocation();

  const user = useSelector(selectUser);
  const isLoading = useSelector(selectIsLoading);
  const isCancelProcessing = useSelector(selectIsCancelProcessing);
  const jobListRowData = useSelector(selectJobListRowData);
  const totalRowCount = useSelector(selectTotalRowCount);
  const isUpdateProcessing = useSelector(selectIsUpdateProcessing);
  const candidates = useSelector(selectUsers);
  const positionTemplateData = useSelector(selectPositionTemplateData);
  const isLoadingPt = useSelector(selectIsLoadingPt);
  const isCreateJobsDialogOpen = useSelector(selectIsCreateJobsDialogOpen);
  const isLoadingPathSelectedJob = useSelector(selectIsLoadingPathSelectedJob);
  const selectedJob = useSelector(selectSelectedJob);
  // Todo Uncomment when BE is ready
  // const selectedInvitations = useSelector(selectInvitations);

  const jobListColumnData = useMemo(() => getJobListColumnData(user.role), [user]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(10);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [createJobType, setCreateJobType] = useState(null);
  const [listData, setListData] = useState(jobListRowData);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [customFilters, setCustomFilters] = useState({
    // initial display joblist is upcoming joblist
    filterModel: {
      items: filters.UPCOMING_JOBS,
    },
  });

  const apiPath = 'job-orders/read';
  const longTermToggle = launchDarklyToggles(flags, 'isLongTermJobCreationEnabled');

  const handleDayJobClick = () => {
    if (positionTemplateData.length === 0) {
      dispatch(showSnackbar({ message: 'Please Create Position Template' }));
    } else {
      dispatch(setIsCreateJobsDialogOpen(true));
    }
  };

  const handleLongTermJobClick = () => {
    if (positionTemplateData.length === 0) {
      dispatch(showSnackbar({ message: 'Please Create Position Template' }));
    } else {
      setIsDrawerOpen(true);
      setCreateJobType('LongTermJob');
    }
  };
  const handleJobUpdate = useCallback(
    (formData) => {
      /*
       * FIXME: This hardcodes for all users what fields they
       * can send. This will fail once some user types are allowed
       * to send data that others are not.
       */
      const data = {
        id: get(['id'], selectedJob),
        fields: { numOpenings: Number(formData.numOpenings) },
      };
      const schemaErrors = validateSchema(data, JobUpdateRequestSchema);
      if (Object.keys(schemaErrors).length === 0) {
        dispatch(updateJob(data));
      }
    },
    [dispatch, selectedJob]
  );

  const onSelectionModelChange = useCallback(
    (job) => {
      dispatch(setSelectedJob(job));

      const newSearchParams = { [searchParamOptions.JOB_ORDER]: get('id', job) };

      if (job) {
        if (location.pathname === '/dashboard') {
          navigate({
            pathname: '/jobs/jobsList',
            search: `?${createSearchParams(newSearchParams)}`,
          });
        } else {
          setSearchParams(newSearchParams);
        }
      }
    },
    [dispatch, location.pathname, navigate, setSearchParams]
  );

  const onDetailClose = useCallback(() => {
    dispatch(setSelectedJob(null));
    setSearchParams({ [searchParamOptions.JOB_ORDER]: undefined });
  }, [dispatch, setSearchParams]);

  useEffect(() => {
    if (
      jobListRowData.length > 0 &&
      listData.length > 0 &&
      jobListRowData[jobListRowData.length - 1].id !== listData[listData.length - 1].id
    ) {
      onDetailClose();
    }
  }, [jobListRowData, listData, onDetailClose]);

  useEffect(() => {
    setListData(jobListRowData);
  }, [jobListRowData]);

  useEffect(() => {
    dispatch(getCorporationsForJobsList());
    dispatch(getPositionTemplate());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  const jobsListPaginationQuery = React.useCallback(
    (params) => dispatch(getJobsList(params)),
    [dispatch]
  );

  const tabData = [
    {
      tabLabel: 'Upcoming',
    },
    {
      tabLabel: 'Completed',
    },
  ];

  const onTabChange = (newVal) => {
    if (newVal === 0) {
      setCustomFilters({
        // upcoming
        filterModel: {
          items: filters.UPCOMING_JOBS,
        },
      });
    }
    if (newVal === 1) {
      setCustomFilters({
        // completed
        filterModel: {
          items: filters.COMPLETED_JOBS,
        },
      });
    }
  };

  const toggleExportOpen = () => {
    setIsExportOpen(true);
  };

  const detailTabData = (container) => [
    {
      tabContent: <Overview container={container} initialValues={selectedJob} user={user} />,
      tabLabel: 'Overview',
    },
    {
      tabContent: <ShiftDetails initialValues={selectedJob} />,
      tabLabel: 'Shift Details',
    },
    {
      tabContent: (
        <RosterForm
          onUpdate={handleJobUpdate}
          initialValues={selectedJob}
          // invitationData={selectedInvitationData}
          user={user}
          candidates={candidates}
        />
      ),
      tabLabel: 'Roster',
    },
  ];

  const mainContainerRef = useRef();
  const rightButtonRef = useRef();
  const leftTabRef = useRef();

  const mainContainerDims = useDims(mainContainerRef);
  const rightButtonDims = useDims(rightButtonRef);
  const leftTabDims = useDims(leftTabRef);
  const token = get('token', user);

  useEffect(() => {
    searchParamSelectIdHandler({
      isLoading,
      isLoadingSelectedItem: isLoadingPathSelectedJob,
      pathId: pathSelectedJobId,
      listData,
      pageSize,
      selectedItem: selectedJob,
      onSelection: onSelectionModelChange,
      getData: (data) => dispatch(getPathSelectedJob(data)),
    });
  }, [
    dispatch,
    isLoading,
    isLoadingPathSelectedJob,
    jobListRowData,
    listData,
    onSelectionModelChange,
    pageSize,
    selectedJob,
    pathSelectedJobId,
    token,
  ]);

  return (
    <RootGrid container>
      <Slide
        direction="right"
        unmountOnExit
        in={!!mediumScreen || (!mediumScreen && !selectedJob)}
        timeout={10}
        easing={{ enter: 'step-end', exit: 'step-start' }}
        ref={mainContainerRef}
      >
        <Grid
          item
          container
          md
          xs
          sm
          sx={{
            height: `calc(100% - ${listHeaderHeight} - ${dataGridFiltersHeight} - ${tabHeight})`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              // If the container is smaller than the tabs + buttons, move the buttons down
              flexDirection:
                mainContainerDims.width - rightButtonDims.width - leftTabDims.width < 0
                  ? 'column'
                  : 'row',
              rowGap: theme.spacing(1),
            }}
          >
            <div ref={leftTabRef}>
              <JobTypeTab initVal="dayJobs" isDetailOpen={!!selectedJob} />
            </div>

            <Box
              sx={{
                display: 'flex',
                height: `${dataGridFiltersHeight}`,
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              ref={rightButtonRef}
            >
              <Box>
                <WaeButton
                  type="text"
                  startIcon={
                    mainContainerDims.width - rightButtonDims.width - leftTabDims.width <
                    0 ? null : (
                      <FileDownloadIcon />
                    )
                  }
                  onClick={toggleExportOpen}
                >
                  {mainContainerDims.width - rightButtonDims.width - leftTabDims.width < 0
                    ? 'Export'
                    : 'Export to CSV'}
                </WaeButton>
              </Box>
              {!isLoadingPt && (
                <WaeButton
                  type="outlined"
                  text="New Bulk Order"
                  startIcon={
                    <AddIcon
                      sx={{
                        color: `${theme.button.palette.primary}`,
                      }}
                    />
                  }
                  onClick={handleDayJobClick}
                  sx={{
                    marginLeft: 1,
                  }}
                />
              )}
              {!isLoadingPt && (
                <WaeButton
                  variant="contained"
                  text="New Job Order"
                  startIcon={<AddIcon />}
                  onClick={handleLongTermJobClick}
                  disabled={!longTermToggle}
                  sx={{
                    marginLeft: 1,
                  }}
                />
              )}
            </Box>
          </Box>
          <Grid
            container
            sx={{
              width: '100%',
              height:
                !largeScreen && !!selectedJob
                  ? `calc(100% - ${listHeaderHeight} - ${dataGridFiltersHeight})`
                  : `calc(100% - ${listHeaderHeight})`,
            }}
          >
            <PaginatedDataGrid
              additiveFilters={additiveFilters}
              apiPath={apiPath}
              columnData={jobListColumnData}
              customFilter={customFilters}
              FilterLeftComponent={
                <Tab data={tabData} onChange={onTabChange} background="default" />
              }
              hideFilterBreakpoint={selectedJob ? '1700px' : '0'}
              isLoading={isLoading}
              onSelectionModelChange={onSelectionModelChange}
              pageSize={pageSize}
              page={page}
              paginatedData={listData}
              pagination
              paginationQuery={jobsListPaginationQuery}
              setPageSize={setPageSize}
              setPage={setPage}
              setRowCount={setRowCount}
              sx={{
                border: 0,
                width: '100%',
                paddingLeft: 1,
              }}
              totalRowCount={totalRowCount}
              selectionModel={[get('id', selectedJob)]}
              rowCount={rowCount}
              style={{ marginRight: theme.spacing(2) }}
            />
            {selectedJob && (
              <DetailView
                close={onDetailClose}
                tabData={detailTabData}
                onUpdate={handleJobUpdate}
                sx={{
                  marginLeft: theme.spacing(1.5),
                  height:
                    !largeScreen && !!selectedJob
                      ? `calc(100% - ${listHeaderHeight} - ${dataGridFiltersHeight})`
                      : `calc(100% - ${listHeaderHeight})`,
                }}
              />
            )}
          </Grid>
        </Grid>
      </Slide>

      {!mediumScreen && !!selectedJob && (
        <DetailView
          close={onDetailClose}
          tabData={detailTabData}
          onUpdate={handleJobUpdate}
          sx={{ marginLeft: theme.spacing(1.5) }}
        />
      )}

      <ExportView open={isExportOpen} close={() => setIsExportOpen(false)} />

      <CancelJobModal
        selectedJob={selectedJob}
        isCancelDialogOpen={isCancelDialogOpen}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
      />
      <AssignEmployeeModal
        selectedJob={selectedJob}
        isAssignDialogOpen={isAssignDialogOpen}
        setIsAssignDialogOpen={setIsAssignDialogOpen}
        candidates={candidates}
      />
      {(isCancelProcessing || isUpdateProcessing) && <BackdropCircularProgress />}
      <CreateJobDrawer
        anchor="right"
        isOpen={isDrawerOpen}
        createJobType={createJobType}
        onClose={() => setIsDrawerOpen(false)}
      />
      {isCreateJobsDialogOpen && (
        <CreateJobsModal
          open={isCreateJobsDialogOpen}
          handleModalClose={() => dispatch(setIsCreateJobsDialogOpen(false))}
        />
      )}
    </RootGrid>
  );
};

JobsList.propTypes = {
  flags: PropTypes.shape({}),
};

export default withLDConsumer()(JobsList);
